"use client";

import { useState } from "react";

interface ContactFormProps {
  dict: {
    nameLabel: string;
    emailLabel: string;
    subjectLabel: string;
    messageLabel: string;
    send: string;
    success: string;
    inquiryTypeLabel: string;
    inquiryGeneral: string;
    inquiryWholesale: string;
    inquiryDistributor: string;
    inquiryPress: string;
    companyLabel: string;
    error: string;
    sending: string;
  };
}

const BUSINESS_INQUIRY_TYPES = ["wholesale", "distributor"];

const inputBaseStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  background: "var(--bp-bg)",
  borderTopWidth: 0,
  borderRightWidth: 0,
  borderLeftWidth: 0,
  borderBottomWidth: 1,
  borderBottomStyle: "solid",
  borderBottomColor: "var(--bp-rule)",
  fontFamily: "var(--bp-sans)",
  fontSize: 15,
  color: "var(--bp-ink)",
  outline: "none",
  borderRadius: 0,
};

const selectStyle: React.CSSProperties = {
  ...inputBaseStyle,
  fontFamily: "var(--bp-mono)",
  fontSize: 12,
  letterSpacing: "0.08em",
  appearance: "none",
  backgroundImage:
    "linear-gradient(45deg, transparent 50%, var(--bp-ink) 50%), linear-gradient(135deg, var(--bp-ink) 50%, transparent 50%)",
  backgroundPosition:
    "calc(100% - 18px) calc(50% - 2px), calc(100% - 13px) calc(50% - 2px)",
  backgroundSize: "5px 5px, 5px 5px",
  backgroundRepeat: "no-repeat",
  paddingRight: 36,
  cursor: "pointer",
  textTransform: "uppercase",
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label className="bp-mono-sm" style={{ color: "var(--bp-ink-2)" }}>
        {label.toUpperCase()}
      </label>
      {children}
    </div>
  );
}

export default function ContactForm({ dict }: ContactFormProps) {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [inquiryType, setInquiryType] = useState("general");

  const showCompanyField = BUSINESS_INQUIRY_TYPES.includes(inquiryType);
  const sending = status === "sending";

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");

    const form = e.currentTarget;
    const data = new FormData(form);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          inquiryType: data.get("inquiryType"),
          company: data.get("company") || undefined,
          subject: data.get("subject"),
          message: data.get("message"),
        }),
      });

      if (!res.ok) throw new Error("Request failed");

      setStatus("success");
      form.reset();
      setInquiryType("general");
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div
        style={{
          padding: 32,
          background: "var(--bp-bg)",
          border: "1px solid var(--bp-ink)",
          textAlign: "center",
        }}
      >
        <span className="bp-mono-sm" style={{ letterSpacing: "0.2em" }}>
          ✓ MESSAGE SENT
        </span>
        <p
          style={{
            marginTop: 16,
            fontFamily: "var(--bp-serif)",
            fontWeight: 300,
            fontSize: 26,
            letterSpacing: "-0.01em",
            lineHeight: 1.2,
          }}
        >
          {dict.success}
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="bp-mono-sm"
          style={{
            marginTop: 24,
            padding: "10px 16px",
            background: "transparent",
            color: "var(--bp-ink)",
            border: "1px solid var(--bp-ink)",
            cursor: "pointer",
          }}
        >
          SEND ANOTHER →
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: "flex", flexDirection: "column", gap: 24 }}
    >
      <Field label={dict.inquiryTypeLabel}>
        <select
          name="inquiryType"
          value={inquiryType}
          onChange={(e) => setInquiryType(e.target.value)}
          disabled={sending}
          style={selectStyle}
        >
          <option value="general">{dict.inquiryGeneral}</option>
          <option value="wholesale">{dict.inquiryWholesale}</option>
          <option value="distributor">{dict.inquiryDistributor}</option>
          <option value="press">{dict.inquiryPress}</option>
        </select>
      </Field>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 24,
        }}
        className="bp-form-row"
      >
        <Field label={dict.nameLabel}>
          <input name="name" required disabled={sending} style={inputBaseStyle} />
        </Field>
        <Field label={dict.emailLabel}>
          <input
            name="email"
            type="email"
            required
            disabled={sending}
            style={inputBaseStyle}
          />
        </Field>
      </div>

      {showCompanyField && (
        <Field label={dict.companyLabel}>
          <input
            name="company"
            required
            disabled={sending}
            placeholder="Your company or clinic name"
            style={inputBaseStyle}
          />
        </Field>
      )}

      <Field label={dict.subjectLabel}>
        <input name="subject" required disabled={sending} style={inputBaseStyle} />
      </Field>

      <Field label={dict.messageLabel}>
        <textarea
          name="message"
          rows={5}
          required
          disabled={sending}
          style={{ ...inputBaseStyle, resize: "vertical", minHeight: 120 }}
        />
      </Field>

      {status === "error" && (
        <p
          className="bp-mono-sm"
          style={{
            color: "oklch(0.55 0.18 25)",
            padding: "10px 14px",
            border: "1px solid oklch(0.55 0.18 25)",
          }}
        >
          {dict.error}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        className="bp-btn"
        style={{
          width: "100%",
          justifyContent: "center",
          padding: "16px 20px",
          opacity: sending ? 0.6 : 1,
          cursor: sending ? "not-allowed" : "pointer",
        }}
      >
        {sending ? `${dict.sending.toUpperCase()}…` : `${dict.send.toUpperCase()} →`}
      </button>

      <style>{`
        input:focus-visible, textarea:focus-visible, select:focus-visible {
          border-bottom-color: var(--bp-ink) !important;
        }
        @media (max-width: 600px) {
          .bp-form-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </form>
  );
}
